/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

const DEFAULT_TIMEOUT_SECONDS = 600;
const EXPONENTIAL_BACKOFF_EXPONENT = 3;

export const fetchRetry = async (
  url: RequestInfo | URL,
  options: RequestInit = {},
  timeoutSeconds: number = DEFAULT_TIMEOUT_SECONDS,
  retries: number = 3,
  retryCount: number = 0,
  exponentialBackoffExponent: number = EXPONENTIAL_BACKOFF_EXPONENT
): Promise<Response> => {
  const requestMethod = (options.method || "GET").toUpperCase();
  try {
    const response: Response = await Promise.race<Response>([
      fetch(url, options),
      new Promise((_resolve, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(`Request timed out after ${timeoutSeconds} seconds.`)
            ),
          timeoutSeconds * 1000
        )
      ),
    ] as Promise<Response>[]);
    if (!response.ok) {
      if ([429, 503].includes(response.status) && retries > 0) {
        retryCount++;
        retries--;
        // wait with exponential backoff
        await sleep(retryCount * exponentialBackoffExponent);
        return fetchRetry(
          url,
          options,
          timeoutSeconds,
          retries,
          retryCount,
          exponentialBackoffExponent
        );
      } else {
        throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
      }
    }
    return response;
  } catch (error: any) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (
      isRetryableNetworkError(errorMessage, requestMethod) &&
      retries > 0
    ) {
      retryCount++;
      retries--;
      // wait with exponential backoff
      await sleep(retryCount ** exponentialBackoffExponent);
      return fetchRetry(
        url,
        options,
        timeoutSeconds,
        retries,
        retryCount,
        exponentialBackoffExponent
      );
    }
    throw error instanceof Error ? error : new Error(errorMessage);
  }
};

function isRetryableNetworkError(message: string, method: string): boolean {
  if (!["GET", "HEAD"].includes(method)) {
    return false;
  }
  return (
    message === "Timeout" ||
    message === "Network request failed" ||
    message === "Failed to fetch" ||
    message.startsWith("Request timed out after")
  );
}

function sleep(seconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}
